import { useState } from "react";

import { Button, Divider, message, Modal, Result } from "antd";
import Dragger from "antd/lib/upload/Dragger";
import { InboxOutlined, StopOutlined, UploadOutlined } from "@ant-design/icons";

import notifier from "utils/notifier";
import { ErrorResponse } from "utils/types";

import { CreateAuthDetailModalLocalProps, CreateAuthDetailModalSingleProps } from "./Proptypes";
import styles from './CreateAuthDetailModal.module.scss';

export const CreateAuthDetailModal = ({ fileList, setFileList, originalFileNames, ...rest }: CreateAuthDetailModalLocalProps) => {

  const types = ['.tif', '.tiff', '.jpg', '.jpeg', '.pdf', '.doc', '.docx','.TIF','.TIFF','.JPG','.JPEG','.PDF','.DOC','.DOCX'];

  const handleRemove = (fileId: string) => {
    setFileList(fileList.filter((item: any) => item.uid !== fileId));
  };

  const handleClearAll = () => {
    setFileList(fileList.filter((file: any) => originalFileNames.includes(file.name)));
  };

  // customRequest to just return a success to attach file to the form instead being actually uploading yet
  const customRequest = ({ onSuccess }: any) => {
    setTimeout(() => onSuccess('Success'), 0);
  };

  const beforeUpload = (file: any) => {
    const ext = file.name.substr(file.name.lastIndexOf('.'));
    if (!types.includes(ext)) {
      message.error(`You can only upload files of type ${types.join(', ')}`);
      return false;
    } else {
      //file attachment used to list out the files under the dragger and cannot have duplicate files attached
      if (fileList.filter((attachment: any) => attachment.name === file.name).length === 0) {
        setFileList((previous: any) => ([...previous, file]));
      } else {
        notifier.error('Please attach a file with a different name.');
      }
      return false;
    };
  };

  return (
    <>
      <Dragger
        fileList={fileList}
        customRequest={customRequest}
        beforeUpload={beforeUpload}
        multiple={true}
        showUploadList={false}
        accept={types.join(',')}
        {...rest}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag a file at a time to this area to upload</p>
        <p className="ant-upload-hint">
          (Only files of type {types.join(', ')} will be accepted)
        </p>

        {fileList.length !== 0 && <Divider />}

        <div className="row">
          <ul className={styles.fileList}>
            {fileList.map((file: any, key: number) => (
              <li key={key}>
                {file.name}{" "}
                {! originalFileNames.includes(file.name) && 
                <span><i onClick={() => handleRemove(file.uid)} className="fa fa-trash" aria-hidden="true"></i></span>}
              </li>
            ))}
          </ul>
        </div>
      </Dragger>

      {fileList.length !== 0 && (
        <div className="d-flex justify-content-end">
          <Button
            type="default"
            onClick={() => handleClearAll()}
            className={styles.clearButton}
          >
            Clear All
          </Button>
        </div>
      )}
    </>
  );
};

export const CreateAuthDetailModalSingle = ({ fileTypes, isUploading, upload, page, selectedIpa, showRemove = true, ...rest }: CreateAuthDetailModalSingleProps) => {

  const [fileList, setFileList] = useState<any[]>([]);

  const handleUpload = () => {

    // check if file list is empty
    if (fileList.length === 0) {
      notifier.error('Please add a file.');
      return;
    }

    const formdata = new FormData();
    if (page === 'files') formdata.append('RmiCompanyId', selectedIpa ? selectedIpa : '');
    formdata.append('RmiUserId', sessionStorage.getItem('user_id')!);
    formdata.append('FileExtensions', fileTypes);
    formdata.append('uploadfile', fileList[0]);

    upload(formdata)
      .then((response: any) => {
        const responseText = response.returnData.split('.');
        Modal.success({
          icon: false,
          width: '750px',
          content: <Result
                    status="success"
                    title={responseText[0] + '.'}
                    subTitle={page === 'test' ? responseText[1] + '. ' + responseText[2] : responseText[1] + '.'}
                  />
        });
        setFileList([]);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const beforeUpload = (file: any) => {
    setFileList([file]);
    return false;
  };

  return (
    <>
      <Dragger
        fileList={fileList}
        accept={fileTypes}
        beforeUpload={beforeUpload}
        multiple={false}
        maxCount={1}
        onRemove={() => setFileList([])}
        {...rest}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag file to this area to upload</p>
        <p className="ant-upload-hint">
        (Only files of type {fileTypes} will be accepted)
        </p>
      </Dragger>

    <div className="btn-wrapper d-flex justify-content-end">
      {showRemove && <Button type="default" className={ styles.buttonWidth + " mr-1"} onClick={() => setFileList([])}><StopOutlined /> Remove</Button> }
      <Button type="primary" className={ styles.buttonWidth} onClick={handleUpload} loading={isUploading}><UploadOutlined /> Submit</Button>
    </div>
  </>
  );
}

export default CreateAuthDetailModal;